import { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import CardGridSkeleton from "../gridCards/animations/CardGridSkeleton";
import { v4 as uuidv4 } from "uuid";
import POSAuthGridCard from "../gridCards/POSAuthGridCard";
import * as Library from "../../../utils/Library";
import moment from "moment";

/* #region  SCOPED CSS */
const StyledPage = styled.div`
    & {
        overflow-y: scroll;
        height: 100%;
        margin-right: -24px;
        margin-left: -24px;
        padding-left: 24px;
        padding-right: 17px;
    }
`;
/*  #endregion */

/**
 * Renders an infinite grid of cards.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {string} [props.className] - The additional CSS class name for the component.
 * @param {number} [props.limit=10] - The maximum number of cards to load per page.
 * @param {string} [props.queryKey] - The query key for fetching data.
 * @param {number} [props.minimumCardsFill] - The minimum number of cards to fill the grid.
 * @param {number} [props.minColumnWidth] - The minimum width of each grid column.
 * @param {Function} [props.onEditAction] - The callback function for the edit action.
 * @param {Function} [props.onDeleteAction] - The callback function for the delete action.
 * @param {Function} [props.onSuspendAction] - The callback function for the suspend action.
 * @param {string} [props.statusFromPath] - The status from the path.
 * @returns {JSX.Element} The rendered component.
 */

export default function InfinityPOSAuthGridCard(props) {
    const pageLimit = props?.limit || 10;

    const gridLayoutStyle = {
        display: "grid",
        gridTemplateColumns: `repeat(auto-fill, minmax(${props?.minColumnWidth || "395px"}, 1fr))`,
        gap: "24px"
    };

    /* #region STATES */
    //const navigate = useNavigate();
    const scrollContainerRef = useRef(null);
    const [scroll, setScroll] = useState(true);

    const [minimumCardsFill, setminimumCardsFill] = useState(1); //props?.minimumCardsFill || pageLimit
    const requestEnabled = props?.requestEnabled ?? true;

    const [queryKey, setQueryKey] = useState(props?.queryKey ?? uuidv4());

    useEffect(() => {
        if (props?.queryKey) setQueryKey(props?.queryKey);
    }, [props?.queryKey]);

    const filters = props?.filters;

    const {
        data,
        isFetching: isLoading,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage
    } = Library.usePostInfiniteQuery(
        queryKey,
        "getAuthorizationsData",
        {
            companyID: localStorage.getItem("currentCompanyID"),
            limit: pageLimit,
            ...filters
        },
        () => {
            setScroll(true);
        },
        false,
        "",
        false,
        requestEnabled
    );

    const pageList = data?.pages;
    //const totalCount = data?.pages[0]?.data?.totalCount;
    const nextDataCount = data?.pages[data?.pages?.length - 1].data?.nextDataCount;
    /* #endregion */

    /* #region EFFECTS */
    useEffect(() => {
        handleScroll();

        setminimumCardsFill(getTotalCards() + 1);
    }, [data?.pages]);

    useEffect(() => {
        const scrollContainer = scrollContainerRef.current;
        if (scrollContainer && !isLoading && scroll && !isFetchingNextPage) {
            scrollContainer.addEventListener("scroll", handleScroll);
        }

        return () => {
            scrollContainer?.removeEventListener("scroll", handleScroll);
        };
    }, [scrollContainerRef, isLoading, pageList, scroll, isFetchingNextPage, data?.pages]);
    /* #endregion */

    /* #region METHODS */
    const handleScroll = () => {
        const scrollContainer = scrollContainerRef.current;

        const isAtBottom =
            Math.floor(scrollContainer.scrollHeight - scrollContainer.scrollTop) <= scrollContainer.clientHeight;

        if (isAtBottom && !isLoading && hasNextPage && !isFetchingNextPage) {
            setScroll(false);
            fetchNextPage();
        }
    };

    const getTotalCards = () => {
        return pageList?.reduce((acc, it) => {
            return acc + it?.data?.result?.length;
        }, 0);
    };

    const formatIsoToDateTime = isoDate => {
        if (!isoDate) return "--";
        const res = moment(isoDate).format("DD/MM/YYYY HH:mm");
        if (res === "Invalid date") return "--";
        return res;
    };

    /* #endregion */

    return (
        <StyledPage className={`library-scrollable ${props?.className || ""}`} ref={scrollContainerRef}>
            {isLoading && !isFetchingNextPage ? (
                <div style={gridLayoutStyle}>
                    {Array.from({ length: 8 }, () => (
                        <CardGridSkeleton
                            key={uuidv4()}
                            height="420.28px"
                            minColumnWidth={props?.minColumnWidth || "439px"}
                        />
                    ))}
                </div>
            ) : (
                <div className="mb-24px" style={gridLayoutStyle}>
                    {pageList?.map(it => {
                        return it?.data?.result?.map(item => {
                            const cardData = item?.authorizations?.[0] || {}; // TODO: receiving multple authorizations from api??
                            const status = cardData?.status;

                            return (
                                <POSAuthGridCard
                                    key={cardData?._id}
                                    image={cardData?.posLogo?.mainLogoURL || ""}
                                    //imageOverlay={cardData?.retailBrand?.logos?.mainLogoURL || ""}
                                    //isImageWithOverlay={true}
                                    title={cardData?.name || "-"}
                                    titlePrefix="AUTHORIZATION NAME"
                                    id={cardData?._id || "--"}
                                    idPrefix="BOTAG ID"
                                    isCardEnabled={true}
                                    isCardSuspended={false}
                                    data={{
                                        createdBy: cardData?.createdBy,
                                        createdOn: formatIsoToDateTime(cardData?.createdAt),
                                        POS: cardData?.posName,
                                        environment: cardData?.environment,
                                        type: cardData?.type,
                                        totalFields: item?.authorizations?.length || 0,
                                        status: status
                                    }}
                                    edit={{
                                        hidden: false,
                                        active: false
                                    }}
                                    suspend={{ isReenable: false, hidden: true, active: false }}
                                    view={{ hidden: false, active: false }}
                                    delete={{ active: true, hidden: false, onClick: () => props?.onDeleteAction(item) }}
                                    primaryButton={{
                                        title: props?.primaryButton?.title ?? "DETAILS",
                                        onClick:
                                            props?.primaryButton?.onClick && !item?.default
                                                ? () => props?.primaryButton?.onClick(item)
                                                : undefined,
                                        isDisabled: props?.primaryButton?.isDisabled
                                    }}
                                    secondaryButton={{ title: "ADD NEW" }}
                                />
                            );
                        });
                    })}

                    {!isFetchingNextPage &&
                        minimumCardsFill - getTotalCards() > 0 &&
                        Array.from({ length: minimumCardsFill - getTotalCards() }, (_, index) => (
                            <POSAuthGridCard
                                key={index}
                                isCardEnabled={false}
                                primaryButton={{ title: "DETAILS" }}
                                secondaryButton={{ title: "ADD NEW" }}
                                idPrefix="BOTAG ID"
                                titlePrefix="AUTHORIZATION NAME"
                            />
                        ))}

                    {isFetchingNextPage &&
                        Array.from({ length: nextDataCount }, (_, index) => (
                            <CardGridSkeleton key={index} height="420.28px" />
                        ))}
                </div>
            )}
        </StyledPage>
    );
}
